import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ArrowDown } from "../../assets/icons/ArrowDown";
import { Modal } from "../Modal";
import { IOption } from "../SelectDropdown";
import {
  CardSummary,
  Placeholder,
  SelectedSummary,
  TriggerBox,
} from "./SnomedSelect.styles";
import { SnomedSelectModal } from "./SnomedSelectModal";

export type SnomedSelectProps = {
  /** Currently selected options (conceptId + "<term> - <conceptId>" label). */
  value: IOption[];
  onChange: (selected: IOption[]) => void;
  /** Server-side search — returns the matching options for a search term. */
  searchOptions: (search: string) => Promise<IOption[]>;
  /** Cache namespace for the search query (e.g. "diagnoses" / "allergies"). */
  queryKey: string;
  modalTitle?: string;
  searchPlaceholder?: string;
  /** Placeholder shown on the trigger when nothing is selected. */
  placeholder?: string;
  /** Trigger width — defaults to filling its container. */
  triggerWidth?: string;
  /** "form" (default) matches the onboarding selects; "card" is the compact patient-card select. */
  triggerVariant?: "form" | "card";
  disabled?: boolean;
  hasError?: boolean;
};

export function SnomedSelect({
  value,
  onChange,
  searchOptions,
  queryKey,
  modalTitle,
  searchPlaceholder,
  placeholder,
  triggerWidth = "100%",
  triggerVariant = "form",
  disabled = false,
  hasError = false,
}: SnomedSelectProps) {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const isCard = triggerVariant === "card";
  const summary = value
    .map((option) => option.label)
    .join(isCard ? "\n" : ", ");

  const openModal = () => {
    if (!disabled) setIsModalOpen(true);
  };

  return (
    <>
      <TriggerBox
        type="button"
        disabled={disabled}
        hasError={hasError}
        compact={isCard}
        onClick={openModal}
        css={{ width: triggerWidth }}
      >
        {value.length === 0 && (
          <Placeholder>{placeholder ?? t("common_select")}</Placeholder>
        )}

        {value.length > 0 &&
          (isCard ? (
            <CardSummary>{summary}</CardSummary>
          ) : (
            <SelectedSummary title={summary}>{summary}</SelectedSummary>
          ))}

        {!isCard && <ArrowDown />}
      </TriggerBox>

      <Modal
        portal
        open={isModalOpen}
        optionsModal={{
          width: "560px",
          maxWidth: "92vw",
          maxHeight: "85vh",
          padding: "24px",
          showCloseButton: true,
        }}
        onOpenChange={(open: boolean) => setIsModalOpen(open)}
      >
        <SnomedSelectModal
          title={modalTitle ?? t("common_select")}
          searchPlaceholder={searchPlaceholder ?? t("common_searchSnomed")}
          searchOptions={searchOptions}
          queryKey={queryKey}
          value={value}
          onChange={onChange}
          onClose={() => setIsModalOpen(false)}
        />
      </Modal>
    </>
  );
}
